import { Telemetry } from '../hooks/useTelemetry';

/**
 * Browser page translation (Chrome/Edge/Safari "Translate this page") and
 * some extensions rewrite text nodes in place — typically wrapping them in
 * `<font>` elements. React still holds references to the original nodes, so
 * the next time it removes or reorders one it calls `removeChild` /
 * `insertBefore` on a parent that no longer owns it, and the browser throws
 * NotFoundError. That throw escapes the reconciler and the ErrorBoundary
 * replaces the student's whole screen.
 *
 * In production this is the single largest CriticalApplicationError bucket
 * ("Failed to execute 'removeChild' on 'Node'", Firefox's "Node.removeChild:
 * …", Safari's "The object can not be found here."), and ~every occurrence
 * carries a `browserTranslation` session attribute. Many students translate
 * on purpose, so blocking translation (`translate="no"`) isn't an option.
 *
 * This is the long-standing community workaround from facebook/react#11538:
 * when the parent doesn't own the node any more, skip the removal (it's
 * already gone from where React thinks it is) and append instead of
 * throwing on insert. React's own view of the tree stays consistent; the
 * cost is that a translated text node may briefly show stale text.
 */
let installed = false;

const report = (method: 'removeChild' | 'insertBefore') => {
  Telemetry()?.addPageAction('DomMutationGuardTriggered', {
    method,
    htmlLang: document.documentElement.lang,
  });
};

export const installDomMutationGuard = () => {
  if (installed || typeof Node !== 'function' || !Node.prototype) return;
  installed = true;

  const originalRemoveChild = Node.prototype.removeChild;
  Node.prototype.removeChild = function <T extends Node>(
    this: Node,
    child: T,
  ): T {
    if (child.parentNode !== this) {
      report('removeChild');
      return child;
    }
    return originalRemoveChild.call(this, child) as T;
  };

  const originalInsertBefore = Node.prototype.insertBefore;
  Node.prototype.insertBefore = function <T extends Node>(
    this: Node,
    newNode: T,
    referenceNode: Node | null,
  ): T {
    if (referenceNode && referenceNode.parentNode !== this) {
      report('insertBefore');
      return this.appendChild(newNode);
    }
    return originalInsertBefore.call(this, newNode, referenceNode) as T;
  };
};
